/**
 * Swipe right/left on a row to indent/outdent (SPEC §8.1):
 * 40 px horizontal commit threshold, < 20° vertical drift, live depth preview, and
 * gestures that start within 24 px of the left screen edge are ignored (iOS back-swipe).
 */
import { useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react'
import { gestureState } from '../store/ui'

const COMMIT = 40
const EDGE = 24
const MAX_ANGLE = Math.tan((20 * Math.PI) / 180)
const START_SLOP = 8

export interface SwipeHandlers {
  onPointerDown: (e: RPointerEvent) => void
  onPointerMove: (e: RPointerEvent) => void
  onPointerUp: (e: RPointerEvent) => void
  onPointerCancel: (e: RPointerEvent) => void
}

export function useSwipe(opts: {
  enabled: boolean
  /** Gestures that start on these elements are left alone (e.g. the text being edited). */
  ignore?: (target: Element) => boolean
  canIndent: () => boolean
  canOutdent: () => boolean
  onIndent: () => void
  onOutdent: () => void
}): { dx: number; intent: 'indent' | 'outdent' | null; handlers: SwipeHandlers; consumedTap: () => boolean } {
  const start = useRef<{ x: number; y: number; id: number; active: boolean; dead: boolean } | null>(null)
  const consumed = useRef(false)
  const [dx, setDx] = useState(0)

  const reset = () => {
    start.current = null
    setDx(0)
    if (gestureState.get() === 'swipe') gestureState.set('none')
  }

  // A row unmounting mid-swipe (e.g. a remote delete) must not leave the app thinking a
  // gesture is still in progress.
  useEffect(
    () => () => {
      if (start.current?.active && gestureState.get() === 'swipe') gestureState.set('none')
    },
    [],
  )

  const handlers: SwipeHandlers = {
    onPointerDown(e) {
      consumed.current = false
      if (!opts.enabled || e.pointerType !== 'touch' || e.clientX < EDGE || opts.ignore?.(e.target as Element)) return
      start.current = { x: e.clientX, y: e.clientY, id: e.pointerId, active: false, dead: false }
    },
    onPointerMove(e) {
      const s = start.current
      if (!s || s.dead || e.pointerId !== s.id) return
      if (gestureState.get() === 'drag') {
        s.dead = true
        setDx(0)
        return
      }
      const mx = e.clientX - s.x
      const my = e.clientY - s.y
      if (!s.active) {
        if (Math.abs(mx) < START_SLOP && Math.abs(my) < START_SLOP) return
        if (Math.abs(my) > Math.abs(mx) * MAX_ANGLE || Math.abs(mx) < START_SLOP) {
          s.dead = true // vertical: let the page scroll
          return
        }
        s.active = true
        gestureState.set('swipe')
      }
      // Too much vertical drift cancels the swipe.
      if (Math.abs(my) > Math.max(24, Math.abs(mx) * MAX_ANGLE * 2)) {
        s.dead = true
        setDx(0)
        gestureState.set('none')
        return
      }
      const allowed = mx > 0 ? opts.canIndent() : opts.canOutdent()
      const clamped = Math.max(-72, Math.min(72, mx))
      setDx(allowed ? clamped : clamped * 0.25)
    },
    onPointerUp(e) {
      const s = start.current
      if (s?.active && e.pointerId === s.id) {
        const mx = e.clientX - s.x
        consumed.current = true
        if (mx >= COMMIT && opts.canIndent()) opts.onIndent()
        else if (mx <= -COMMIT && opts.canOutdent()) opts.onOutdent()
      }
      reset()
    },
    onPointerCancel() {
      reset()
    },
  }

  const intent = dx >= COMMIT ? 'indent' : dx <= -COMMIT ? 'outdent' : null
  return { dx, intent, handlers, consumedTap: () => consumed.current }
}
